import { FC, useState, useEffect } from 'react'
import shallow from 'zustand/shallow'
import useEmployeesAndAppraisals from '../../../graphql/employee/hooks/useEmployeesAndAppraisals'
import { useAuth } from '../../../context/auth-context'
import { CompanySettingsAppraisalTable } from './AppraisalTable'
import { CompanySettingsAppraisalInfoBox } from './AppraisalInfoBox'
import { useBUEmployeesAndAppraisals } from '../../../graphql/employee/hooks/useBUEmployeesAndAppraisals'
import { useCompanySettingsStore } from '../CompanySettingsStore'
import { RoleName, EmployeesAndAppraisalsFragment, Country, EmployeeLevelEnum } from '../../../types'
import { useUserManagersRolesByCompany } from '../../../graphql/company/hooks/useUserManagerRolesByCompany'
import {
  AppraisalTabEmployeeFilter,
  filterEmployeeOfAppraisalTab,
  transformAppraisalData,
  ExtendedEmployeesAndAppraisalsFragment,
  appraisalTabInitialFilter,
} from './appraisal-util'
import { CompanyCountrySelectorValue } from '../types'
import { getOldestSyncUTCDate } from '../../employees/util'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'

export const CompanySettingsAppraisalTab: FC = () => {
  const [selected] = useCompanySettingsStore((state) => [state.selected], shallow)
  const { t } = useTranslation()
  const { data, error, loading } = useEmployeesAndAppraisals(selected.companyId, selected.country)
  const { data: userRolesData } = useUserManagersRolesByCompany(selected.companyId)
  const userRoles: RoleName[] =
    userRolesData && userRolesData.findUserManagerRolesByCompany ? userRolesData.findUserManagerRolesByCompany : []
  const isCompanyManager = userRoles.includes(RoleName.CompanyManager)

  /*
  useEffect(() => {
    startPolling(10000)
    return () => {
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.country, selected.companyId])
  */

  if (
    loading ||
    error ||
    !data ||
    !data.nonManagerEmployeesOfCompanyWithCurrentAppraisalMeeting ||
    !data.currentAppraisalSeason ||
    !data.employeeLevels
  ) {
    return (
      <BackgroundBox r height="withTabs">
        <NewLoadingAndErrors
          isLoading={loading}
          hasNoData={
            !data || !data.nonManagerEmployeesOfCompanyWithCurrentAppraisalMeeting || !data.currentAppraisalSeason || !data.employeeLevels
          }
          error={error}
          showSpinner
          t={t}
        />
      </BackgroundBox>
    )
  }

  return (
    <CompanySettingsAppraisalTabContent
      employees={data.nonManagerEmployeesOfCompanyWithCurrentAppraisalMeeting}
      employeeLevels={data.employeeLevels}
      currentAppraisalSeasonYear={data.currentAppraisalSeason.year}
      isCompanyManager={isCompanyManager}
      selected={selected}
    />
  )
}

export const CompanySettingsAppraisalTabContent: FC<{
  employees: EmployeesAndAppraisalsFragment[]
  employeeLevels: EmployeeLevelEnum[]
  currentAppraisalSeasonYear: number
  selected: CompanyCountrySelectorValue
  isCompanyManager: boolean
}> = ({ employees, employeeLevels, currentAppraisalSeasonYear, selected, isCompanyManager }) => {
  const { user } = useAuth()

  const isHrAdmin = !!user.roles && user.roles.filter((role) => role.toString() === `HR_ADMIN_${selected.country}`).length > 0

  const [employeeFilter, setEmployeeFilter] = useState<AppraisalTabEmployeeFilter>(appraisalTabInitialFilter)
  const [tableData, setTableData] = useState<ExtendedEmployeesAndAppraisalsFragment[]>(transformAppraisalData(employees))

  useEffect(() => {
    setTableData((prev) => {
      const newData = transformAppraisalData(employees)
      return prev.map((row) => {
        const updatedRow = newData.find((r) => r.id === row.id)
        return updatedRow ? { ...updatedRow, otherAppraisals: row.otherAppraisals || [] } : row
      })
    })
    setEmployeeFilter(appraisalTabInitialFilter)
  }, [employees])

  const filteredEmployees = filterEmployeeOfAppraisalTab(employeeFilter, tableData)
  const oldestSyncUTCDate: string | undefined = getOldestSyncUTCDate(employees)

  return (
    <>
      <CompanySettingsAppraisalInfoBox
        filteredEmployees={filteredEmployees.length}
        currentAppraisalSeasonYear={currentAppraisalSeasonYear}
        employeeLevels={employeeLevels}
        tableData={tableData}
        employeeFilter={employeeFilter}
        setEmployeeFilter={setEmployeeFilter}
        country={selected.country}
        companyOrBuId={selected.companyId}
        oldestSyncUTCDate={oldestSyncUTCDate}
      />
      <CompanySettingsAppraisalTable
        noData={employees.length === 0}
        filteredEmployees={filteredEmployees}
        tableData={tableData}
        setTableData={setTableData}
        companyId={selected.companyId}
        country={selected.country}
        isHrAdmin={isHrAdmin}
        isCompanyManager={isCompanyManager}
      />
    </>
  )
}

export const CompanySettingsBuAppraisalTab: FC<{ businessUnitId: string }> = ({ businessUnitId }: { businessUnitId: string }) => {
  const { data, error, loading } = useBUEmployeesAndAppraisals(businessUnitId, Country.All)
  const { t } = useTranslation()

  if (loading || error || !data || !data.nonManagerEmployeesOfBusinessUnitWithCurrentAppraisalMeeting || !data.employeeLevels) {
    return (
      <BackgroundBox r height="withTabs">
        <NewLoadingAndErrors
          isLoading={loading}
          hasNoData={!data || !data.nonManagerEmployeesOfBusinessUnitWithCurrentAppraisalMeeting || !data.employeeLevels}
          error={error}
          showSpinner
          t={t}
        />
      </BackgroundBox>
    )
  }

  return (
    <CompanySettingsBuAppraisalTabContent
      employees={data.nonManagerEmployeesOfBusinessUnitWithCurrentAppraisalMeeting}
      employeeLevels={data.employeeLevels}
      buId={businessUnitId}
    />
  )
}

export const CompanySettingsBuAppraisalTabContent: FC<{
  employees: EmployeesAndAppraisalsFragment[]
  employeeLevels: EmployeeLevelEnum[]
  buId: string
}> = ({ employeeLevels, employees, buId }) => {
  const [employeeFilter, setEmployeeFilter] = useState<AppraisalTabEmployeeFilter>(appraisalTabInitialFilter)
  const [tableData, setTableData] = useState<ExtendedEmployeesAndAppraisalsFragment[]>(transformAppraisalData(employees))

  useEffect(() => {
    setTableData((prev) => {
      const newData = transformAppraisalData(employees)
      return prev.map((row) => {
        const updatedRow = newData.find((r) => r.id === row.id)
        return updatedRow ? { ...updatedRow, otherAppraisals: row.otherAppraisals || [] } : row
      })
    })
    setEmployeeFilter(appraisalTabInitialFilter)
  }, [employees])

  const filteredEmployees = filterEmployeeOfAppraisalTab(employeeFilter, tableData)
  const oldestSyncUTCDate: string | undefined = getOldestSyncUTCDate(employees)

  return (
    <>
      <CompanySettingsAppraisalInfoBox
        filteredEmployees={filteredEmployees.length}
        employeeLevels={employeeLevels}
        tableData={tableData}
        employeeFilter={employeeFilter}
        setEmployeeFilter={setEmployeeFilter}
        companyOrBuId={buId}
        oldestSyncUTCDate={oldestSyncUTCDate}
      />
      <CompanySettingsAppraisalTable
        noData={employees.length === 0}
        filteredEmployees={filteredEmployees}
        tableData={tableData}
        setTableData={setTableData}
        isHrAdmin={false}
        isCompanyManager={false}
        buId={buId}
      />
    </>
  )
}
